<script setup>
import { onMounted, onUnmounted, ref, defineAsyncComponent, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useNavigationStore } from '@/stores/navigation'
import { useResizeNavigation } from '@/composables/useResizeNavigation'
import AppLink from '@/components/layout/AppLink.vue'

const NavigationSearch = defineAsyncComponent(() =>
  import('@/components/layout/NavigationSearch.vue')
)
const NavigationServices = defineAsyncComponent(() =>
  import('@/components/layout/NavigationServices.vue')
)

const navigationStore = useNavigationStore()
const { menuShow, servicesShow, searchShow } = storeToRefs(navigationStore)
const { openMenu, openServices, openSearch, fetchMenu } = navigationStore

const navContent = ref(null)
const navWrapper = ref(null)
const menu = ref(null)
const navPreloadKey = ref('')
const hideSearch = ref(false)

const { getMenu, resizeNavigation } = useResizeNavigation({
  navContent,
  navWrapper,
  rightMargin: 350
})

const styleMenu = (styles) => {
  return {
    backgroundColor: styles?.background_color ?? null,
    color: styles?.text_color ?? null
  }
}

const closeMobileNavWhenIsDesktop = () => {
  const isDesktop = window.innerWidth > 1372
  if (menuShow.value && isDesktop) {
    openMenu()
  }
}

const submenu = computed(() => getMenu.value.filter((el) => el.submenu))
const otherMenu = computed(() => getMenu.value.filter((el) => !el.submenu))

const resizeNav = () => {
  const widescreen = window.innerWidth >= 1372
  resizeNavigation()
  closeMobileNavWhenIsDesktop()
  menu.value = widescreen ? getMenu.value : otherMenu.value.concat(submenu.value)
}

const forcePreloadNav = () => {
  navPreloadKey.value = (Math.random() + 1).toString(36).substring(7)
}

onMounted(() => {
  forcePreloadNav()
  fetchMenu().then(() => {
    resizeNav()
    window.addEventListener('resize', resizeNav)
    window.addEventListener('fullscreenchange', forcePreloadNav)
  })
})

onUnmounted(() => {
  window.removeEventListener('resize', resizeNav)
  window.removeEventListener('fullscreenchange', forcePreloadNav)
})
</script>
<template>
  <nav class="nav__content" ref="navContent" :key="navPreloadKey">
    <div class="nav__wrapper" ref="navWrapper">
      <div :class="['nav__icon', { open: menuShow }]" @click="openMenu">
        <span class="nav__element"></span>
        <span class="nav__element"></span>
        <span class="nav__element"></span>
        <span class="nav__element"></span>
        <span class="nav__element"></span>
        <span class="nav__element"></span>
      </div>
      <div class="nav__menu" @click="openServices" v-if="false">
        <a href="#" class="nav__image" rel="nofollow">
          <svg xmlns="http://www.w3.org/2000/svg" width="51" height="31" viewBox="0 0 51 31">
            <g fill="none" fill-rule="evenodd">
              <path
                fill="#474747"
                d="M17 0h4v4h-4zM23 0h4v4h-4zM29 0h4v4h-4zM17 6h4v4h-4zM23 6h4v4h-4zM29 6h4v4h-4zM17 12h4v4h-4zM23 12h4v4h-4zM29 12h4v4h-4z"
              ></path>
              <text
                fill="#757575"
                font-family="Lato-Regular, Lato"
                font-size="8"
                transform="translate(-1)"
              >
                <tspan x="1.244" y="30">SERWISY TVP</tspan>
              </text>
            </g>
          </svg>
        </a>
      </div>
      <transition name="slide-fade">
        <navigation-services v-if="servicesShow" />
      </transition>
      <a href="/" class="nav__logo">
        <img
          src="../../assets/images/logo/tvp_info-2024.svg"
          class="img-responsive logo"
          alt="logo"
        />
      </a>

      <div :class="['nav__navigation', { open: menuShow }]">
        <ul>
          <template v-for="(elMenu, idx) in menu" :key="idx">
            <li
              v-if="!elMenu.styles.icon"
              :id="`navid-${elMenu._id}`"
              :class="[`nav__table`, { nav__more: elMenu.submenu && elMenu.submenu.length > 0 }]"
              @click="elMenu.active = false"
              @mouseover="elMenu.active = true"
              @mouseleave="elMenu.active = false"
            >
              <app-link
                v-show="!elMenu.hide"
                :class="`nav__reference ${elMenu.class || ''}`"
                :to="elMenu.url"
                :routing-data="elMenu.routing_data"
                :path-type="elMenu.type"
                :style="{ color: elMenu.text_color ?? '' }"
                @click="openMenu"
              >
                <span v-html="elMenu.title" />
                <template v-if="elMenu.submenu && elMenu.submenu.length > 0">
                  <template v-if="!elMenu.hideSubmenu">
                    <i class="icon-down-micro nav__down" /><i class="icon-up-micro nav__up" />
                  </template>
                </template>
              </app-link>
              <template v-if="elMenu.submenu && elMenu.submenu.length > 0 && !elMenu.hideSubmenu">
                <ul :class="[`nav__submenu`, { nav__open: elMenu.active }]">
                  <template v-for="(subMenu, idx) in elMenu.submenu" :key="idx">
                    <li
                      :class="[
                        `nav__table`,
                        { nav__program: subMenu.title === 'Wszystkie programy' }
                      ]"
                    >
                      <app-link
                        :class="`nav__reference ${elMenu.class || ''}`"
                        :to="subMenu.url"
                        :routing-data="subMenu.routing_data"
                        :path-type="subMenu.type"
                        :website-id="subMenu.website_id"
                        @click="openMenu"
                      >
                        <span v-html="subMenu.title" />
                      </app-link>
                    </li>
                  </template>
                </ul>
              </template>
            </li>
            <li v-else class="nav__table nav__button-wrapper" :id="`navid-${elMenu._id}`">
              <div class="nav__reference">
                <app-link
                  :style="styleMenu(elMenu.styles)"
                  class="nav__button"
                  :to="elMenu.url"
                  :routing-data="elMenu.routing_data"
                  :path-type="elMenu.type"
                  @click="openMenu"
                >
                  <span class="nav__icon-dynamic" />
                  <span v-html="elMenu.title" />
                </app-link>
              </div>
            </li>
          </template>
        </ul>
      </div>
    </div>
    <div class="nav__loup-wrapper" v-if="!hideSearch">
      <button @click="openSearch" :class="['open', { 'remove-bar': searchShow }]">
        <i :class="['icon-search', { 'new-search--close': searchShow }]"></i>
      </button>
    </div>
    <div class="nav__desktop">
      <div class="nav__social">
        <a href="https://www.facebook.com/tvp.info" class="nav__graphics facebook" target="_blank">
          <i class="social-icon icon-facebook" />
        </a>
        <a href="http://twitter.com/tvp_info" class="nav__graphics twitter" target="_blank">
          <i class="social-icon icon-twitter-new" />
        </a>
        <a
          href="https://www.youtube.com/user/YTtvpinfo" class="nav__graphics youtube" target="_blank">
          <i class="social-icon icon-youtube"></i>
        </a>
        <a href="https://www.instagram.com/tvp.info/" class="nav__graphics instagram" target="_blank">
          <i class="social-icon icon-instagram" />
        </a>
        <a href="https://www.tiktok.com/@tvp.info" class="nav__graphics tiktok" target="_blank">
          <i class="social-icon icon-tiktok" />
        </a>
        <a href="https://bsky.app/profile/tvp.info" class="nav__graphics bluesky" target="_blank">
          <i class="social-icon icon-bluesky" />
        </a>
      </div>
    </div>
    <transition name="slide-fade">
      <navigation-search v-if="searchShow" />
    </transition>
  </nav>
</template>

<style lang="scss">
@import '@/assets/scss/components/layout/navigation-layout.scss';
</style>
